import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  model,
  resource,
  Signal,
  signal,
  untracked,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroupDirective, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrForm, ClrLoadingState } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { Subject } from 'rxjs';
import { Table } from 'src/app/models/ui/table';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';
import { compareDate, CompareDatePipe } from 'src/app/pipes/compare-date.pipe';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LinieDrehungPipe } from 'src/app/pipes/linie-drehung.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ObjTypePipe as objtype } from 'src/app/pipes/objtype.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { MangelMediaApiFuncService } from 'src/app/services/MediaApiFunc/mangel-media-api-func.service';
import { calcDirection, LinieValidationManager } from 'src/app/shared/classes/linie-validation-manager';
import { HilfeBeschreibungModalComponent } from '../../_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { PreviewThumbnailsComponent } from '../../_shared/preview-thumbnails/preview-thumbnails.component';
import { CachedMediaStore } from 'src/app/shared/stores/media-cached.store';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { ConfirmService } from 'src/app/services/confirm.service';
import {
  BaseDataTable,
  BaseItem,
  BaseItemsWrapperComponent,
} from '../../_modals/baseitems-wrapper/baseitems-wrapper.component';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import {
  BaumSelect,
  BaumSelectUsed,
  Bedingung,
  EMPTY_USED_SELECT,
  joinIds,
  MangelBaumNode,
  MangelBaumNodeModel,
} from '../mangelbaum/mangelbaum.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroupOfType } from 'src/app/types/shared';
import {
  BaumMode,
  GleisGroup,
  HiddenGroup,
  LinieGroup,
  MangelFormControls,
  MangelGroup,
  MangelFormModel,
  MangelRowModel,
  MangelRowType,
  SpezRow,
  MangelModel,
} from './mangel-erfassung.types';
import { differenceBy, errorHandle, matchBy, partition } from 'src/app/misc/functions';
import { Dispatcher, event } from '@ngrx/signals/events';
import { type } from '@ngrx/signals';
import { calcPosition, GeoAutofill } from 'src/app/services/Shared/geo-autofill';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-mangel-dialog',
  templateUrl: './mangeldialog.component.html',
  styleUrls: ['./mangeldialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PreviewThumbnailsComponent,
    HilfeBeschreibungModalComponent,
    BaseItemsWrapperComponent,
    DatalistInputComponent,
    GetByCallbackPipe,
    TablePrettyPrintPipe,
    ArrayMethodsCallbackPipe,
    CompareDatePipe,
    LinieDrehungPipe,
    MrTranslatePipe,
    objtype,
    HideIDColumnsPipe,
  ],
  providers: [
    CompareDatePipe,
    LinieDrehungPipe,
    { provide: MediaApiFunc, useClass: MangelMediaApiFuncService },
    CachedMediaStore,
    GeoAutofill,
  ],
})
export class MangelDialogComponent extends LinieValidationManager {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly confirmService = inject(ConfirmService);
  private readonly cachedMedia = inject(CachedMediaStore);
  protected readonly drehung = inject(LinieDrehungService);
  protected readonly drehungPipe = inject(LinieDrehungPipe);
  private readonly dispatcher = inject(Dispatcher);
  private readonly geoAutofill = inject(GeoAutofill);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  public closedialog = new Subject<boolean>();
  private dataRefresh: Subject<void>;

  private readonly preview = viewChild(PreviewThumbnailsComponent);
  private readonly clrForm = viewChild('mangelForm', { read: ClrForm });
  private readonly formGroup = viewChild('mangelForm', { read: FormGroupDirective });

  //#region inputs & set data
  readonly typ_Line_Point = model.required<number>();
  readonly OTYPID = model.required<number>();
  readonly OSTAMMID = model.required<number>();
  readonly OPRUEFID = model.required<number>();
  readonly stammData = model.required<{ startmeter: number; endmeter: number }>();
  readonly KPRUVID = model<number>(1);
  readonly isInspektion = input(false);
  readonly datum = input<string>(undefined);

  public set data({ id: dataIn, dataRefresh }: any) {
    const { mangel, typ_Line_Point, otypid, ostammid, opruefid, kprtypid, startmeter, endmeter } = dataIn;
    this.historie = mangel;
    this.typ_Line_Point.set(typ_Line_Point);
    this.OTYPID.set(otypid);
    this.OSTAMMID.set(ostammid);
    this.OPRUEFID.set(opruefid);
    this.KPRUVID.set(kprtypid);
    this.stammData.set({ startmeter, endmeter });

    this.dataRefresh = dataRefresh;
  }
  //#endregion

  private readonly maengelRs = this.apiService.getMaengelInInspektion(() =>
    this.isInspektion() ?
      {
        OPRUEFID: this.OPRUEFID(),
        OSTAMMID: this.OSTAMMID(),
        OTYPID: this.OTYPID(),
        typ_Line_Point: this.typ_Line_Point(),
      }
    : undefined
  );

  //#region Options
  private readonly typSettingsRs = this.apiService.getTypOptions(
    this.OTYPID,
    'Opt_I_Fehlerklasse',
    'Opt_I_Zustaend_Abt',
    'Opt_I_CheckList'
  );
  protected readonly useFK = computed(() =>
    this.typSettingsRs.hasValue() ? this.typSettingsRs.value().Opt_I_Fehlerklasse : false
  );
  protected readonly useAbteilung = computed(() =>
    this.typSettingsRs.hasValue() ? this.typSettingsRs.value().Opt_I_Zustaend_Abt : false
  );
  protected readonly useChecklist = computed(() =>
    this.typSettingsRs.hasValue() ? this.typSettingsRs.value().Opt_I_CheckList : false
  );

  private readonly interfaceSettingsRs = this.apiService.getInterfaceOptions('optSAPFieldEquipment');
  protected readonly useEquipment = computed(() =>
    this.interfaceSettingsRs.hasValue() ? this.interfaceSettingsRs.value().optSAPFieldEquipment : false
  );

  private readonly settingsRs = this.apiService.getOptions('optFk1_IstGleich_BG', 'optDownChangeFK');
  protected readonly gefahrFK = computed(() =>
    this.settingsRs.hasValue() ? this.settingsRs.value().optFk1_IstGleich_BG : false
  );
  protected readonly sperrFK = computed(() =>
    this.settingsRs.hasValue() ? this.settingsRs.value().optDownChangeFK : false
  );

  private readonly minFK = computed(() => this.rangeFK()[0]);
  //#endregion

  //#region MangelBaum & Select
  /** @property startPunkt für MangelBaum (Gattungen oder Prüfarten) */
  private readonly treeLevel0 = resource({
    params: () => this.OTYPID(),
    loader: ({ params: otypid }) => this.apiService.getMangelBaum(otypid),
    defaultValue: [],
  });
  protected readonly listFiltered: Record<BaumMode, WritableSignal<MangelBaumNode[]>> = {
    Gattung: linkedSignal(() => (this.typ_Line_Point() == 4 ? this.treeLevel0.value() : [])),
    Stamm: signal([]),
    Pruefart: linkedSignal(() => (this.typ_Line_Point() != 4 ? this.treeLevel0.value() : [])),
    Lage: signal([]),
    Mangel: signal([]),
    Instandsetzung: signal([]),
    FK: signal([]),
  };

  private readonly selectResource = this.apiService.getMangelBaumSelect(this.OTYPID);
  protected readonly listRest: Record<Exclude<BaumMode, 'FK'>, Signal<BaseItem[]>> = {
    Gattung: this.restOfItems('Gattung', 'Gattungen'),
    Stamm: this.restOfItems('Stamm', 'StammItems', () => this.formValue.Gattung()?.ID),
    Pruefart: this.restOfItems('Pruefart', 'Prüfarten'),
    Lage: this.restOfItems('Lage', 'Lagen'),
    Mangel: this.restOfItems('Mangel', 'Mängel'),
    Instandsetzung: this.restOfItems('Instandsetzung', 'Instandsetzungen'),
  };

  protected readonly rangeFK = this.apiService.getRangeFK();

  protected readonly meh = computed(() => this.selectResource.value().Mengeneinheiten);
  protected readonly equipments = computed(() => this.selectResource.value().Equipment);

  private readonly usedRs = this.apiService.getMangelBaumUsed();
  protected readonly used = computed<BaumSelectUsed>(() =>
    this.usedRs.hasValue() ? this.usedRs.value() : EMPTY_USED_SELECT
  );

  private readonly bauartgruppeRs = this.apiService.getBauartgruppe(() =>
    this.typ_Line_Point() === 3 ? this.OSTAMMID() : undefined
  );
  private readonly spezResouce = this.apiService.getSpezForStamm(() =>
    objtype.isLine(this.typ_Line_Point()) ? this.OSTAMMID() : undefined
  );
  //#endregion

  //#region properties
  /** @property Schritt -> nächsten Schritt im Kette */
  private readonly baumKette = computed(() => {
    const kette: [BaumMode, BaumMode][] =
      this.typ_Line_Point() == 4 ?
        [
          ['Gattung', 'Stamm'],
          ['Stamm', 'Pruefart'],
          ['Pruefart', 'Mangel'],
          ['Mangel', 'Instandsetzung'],
          ['Instandsetzung', 'FK'],
        ]
      : [
          ['Pruefart', 'Lage'],
          ['Lage', 'Mangel'],
          ['Mangel', 'Instandsetzung'],
          ['Instandsetzung', 'FK'],
        ];
    return new Map<BaumMode, BaumMode>(kette);
  });

  protected historie: Table;
  protected readonly newlyCreated = signal<Table>(TABLE_EMPTY());

  protected readonly table = computed(() => (this.maengelRs.hasValue() ? this.maengelRs.value() : TABLE_EMPTY()));
  #_tableError = effect(() => {
    const er = this.maengelRs.error();
    if (er) this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mängel konnten nicht geladen werden'));
  });
  protected readonly selected = signal<MangelRowType[]>([]);
  protected readonly selectedMangel = computed(() => this.selected()[0]);
  protected readonly OMAZSID = computed(() => this.selectedMangel()?.OMAZSID);
  protected readonly isLoading = signal(false);
  private isInitializing = false;
  private isSaving = false;

  protected readonly saveState = signal(ClrLoadingState.DEFAULT);
  protected readonly geoState = signal(ClrLoadingState.DEFAULT);

  protected readonly isBaseModalOpen = signal(false);
  protected readonly isHelperModalOpen = signal(false);
  protected readonly openedHelperId = signal<number>(null);

  /** @property FK nach unter nicht änderbar */
  protected readonly sperrungFK = linkedSignal(() => (this.selectedMangel(), this.formValue.autoFK(), false));
  protected readonly isMangelValidStatus = computed(() =>
    this.table().rows.every(row => {
      if (this.useChecklist()) {
        return row['nIO'] || row['iO'];
      }
      return compareDate(row.Status, 'sameOrAfter', 'day', this.datum());
    })
  );
  protected readonly canCreateMangelBaumNode = linkedSignal(
    () => this.OMAZSID() && this.nIO() && !this.selectedMangel().SZUMZID
  );
  private readonly startmeter = computed(() => this.stammData().startmeter ?? 0);
  private readonly endmeter = computed(() => this.stammData().endmeter ?? 0);
  protected readonly direction = computed(() => calcDirection(this.startmeter(), this.endmeter()));

  protected deletedFiles: unknown[] = [];

  protected exceptStartEnd = (col: any) => !['von', 'bis'].includes(col.id);
  protected isNotProzent = (umfang: number) => !umfang || umfang < 1 || umfang > 100;
  //#endregion

  //#region Form
  /** @formGroup die meiste Mangel Werte sammelt, wird separat für Punkte in Inspektion mit nIO gesteuert */
  protected mangelGr = new FormGroupOfType<MangelGroup>({
    Lage: new FormControl(null), //* Dynamisch required if not Gleis
    Mangel: new FormControl(null, Validators.required),
    Umfang: new FormControl(null, { updateOn: 'blur' }),
    Mengeneinheit: new FormControl(null),
    Instandsetzung: new FormControl(null, Validators.required),
    FK: new FormControl(null), //* Dynamisch required if useFK
    Betriebsgefahr: new FormControl(false, {
      validators: Validators.required,
      nonNullable: true,
    }),
    Abteilung: new FormControl(null), //* Dynamisch required if useAbteilung
    Equipment: new FormControl(null), //* Dynamisch required if useEquipment
    LVPos: new FormControl(null),
    Bemerkung: new FormControl(null),
    ZFeld1: new FormControl(null),
    ZFeld2: new FormControl(null),
    ZFeld3: new FormControl(null),
    ZFeld4: new FormControl(null),
    ZFeld5: new FormControl(null),
  });

  /** @formGroup die nicht für User angezeigt ist */
  private hiddenGr = new FormGroupOfType<HiddenGroup>({
    SZUMZID: new FormControl(0, { nonNullable: true }),
    autoFK: new FormControl(null),
    lfNr: new FormControl(null),
    Faktor: new FormControl(1, { nonNullable: true }),
  });

  /** @formGroup für Start- & Endmeter */
  protected linieGr = new FormGroupOfType<LinieGroup>({
    //* Dynamisch Validators durch LinieValidationManager
    von: new FormControl(null),
    bis: new FormControl(null),
  });

  /** @formGroup für Gleise */
  protected gleisGr = new FormGroupOfType<GleisGroup>({
    Gattung: new FormControl(null, Validators.required),
    Stamm: new FormControl(null, Validators.required),
    LageGleis: new FormControl(null),
  });

  /** @formGroup Hauptform, andere werden mit ihr verbunden */
  protected formTopGroup = new FormGroupOfType<MangelFormModel>({
    Pruefart: new FormControl(null, Validators.required),
    /** @formControl für Punkte, kontrolliert mangerGr */
    nIO: new FormControl(null), //* Dynamisch required if useChecklist
    mangelGr: this.mangelGr,
    hiddenGr: this.hiddenGr,
    linieGr: this.linieGr,
    gleisGr: this.gleisGr,
  });

  protected readonly formValue = this.catchFormValues();
  private readonly nIO = computed(() =>
    this.isInspektion() && objtype.isPoint(this.typ_Line_Point()) ? this.formValue.nIO() : true
  );

  protected get rowModel(): MangelModel {
    if (this.formTopGroup.invalid) return null;

    const { Pruefart, gleisGr, linieGr, mangelGr, hiddenGr } = this.formTopGroup.getRawValue();
    return {
      ...hiddenGr,
      ...linieGr,
      ...mangelGr,
      OMAZSID: this.OMAZSID(),
      OSTAMMID: this.OSTAMMID(),
      OTYPID: this.OTYPID(),
      typ_Line_Point: this.typ_Line_Point(),
      OPRUEFID: this.OPRUEFID(),
      nIO: this.nIO() ?? false,
      Gattung: this.typ_Line_Point() == 4 ? gleisGr.Gattung.Bezeichnung : null,
      Stamm: this.typ_Line_Point() == 4 ? gleisGr.Stamm.Bezeichnung : null,
      Pruefart: Pruefart.Bezeichnung,
      Lage: this.typ_Line_Point() == 4 ? gleisGr.LageGleis : mangelGr.Lage?.Bezeichnung,
      Mangel: mangelGr.Mangel?.Bezeichnung,
      Instandsetzung: mangelGr.Instandsetzung?.Bezeichnung,
    };
  }
  //#endregion

  //#region Init
  constructor() {
    //* LinieValidationManager
    super({ start: 'von', end: 'bis' }, true);
    effect(() => {
      if (!(this.startmeter() - this.endmeter())) return;
      this.initLinieValidators(this.startmeter(), this.endmeter(), !this.isInspektion(), this.linieGr);
    });

    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    this.geoAutofill.registerOSTAMMID(this.OSTAMMID);

    effect(() => {
      const typ = objtype.is(this.typ_Line_Point());
      this.activateControlIf('nIO', this.isInspektion() && typ == 'point');
      this.activateControlIf('linieGr', typ == 'line');
      this.activateControlIf('gleisGr', this.typ_Line_Point() === 4);
      this.activateControlIf('mangelGr', this.nIO());
    });

    const { Lage, FK, Abteilung, Equipment } = this.mangelGr.controls;
    effect(() => Lage.setValidators(this.typ_Line_Point() != 4 ? Validators.required : null));
    effect(() => FK.setValidators(this.useFK() ? Validators.required : null));
    effect(() => Abteilung.setValidators(this.useAbteilung() ? Validators.required : null));
    effect(() => Equipment.setValidators(this.useEquipment() ? Validators.required : null));
    effect(() => this.formTopGroup.controls.nIO.setValidators(this.useChecklist() ? Validators.required : null));

    this.catchChangesEffects();

    effect(() => {
      const selected = this.selectedMangel();
      untracked(() => this.parseValue(selected));
    });

    const onceEff = effect(() => {
      if (!this.table().rows.length) return;
      untracked(() => this.findDuplicates());
      onceEff.destroy();
    });
  }

  //#endregion

  //#region Catch Changes
  private async parseValue(row: MangelRowType) {
    if (!row || this.isSaving) return;
    this.isInitializing = true;
    this.isLoading.set(true);

    this.cachedMedia.getBilder(row.OMAZSID, row.Bild, this.preview());
    const eintrag = await this.getBaumEintrag(row.SZUMZID);
    this.formTopGroup.reset({
      nIO: this.useChecklist() && !row.iO && !row.nIO ? null : row.nIO,
      linieGr: {
        von: row.von,
        bis: row.bis,
      },
      gleisGr: {
        Gattung: { Bezeichnung: row.Gattung as string, ID: undefined },
        Stamm: { Bezeichnung: row.Stamm, ID: undefined },
        LageGleis: row.Lage,
      },
      hiddenGr: {
        Faktor: row.Faktor,
        lfNr: row.lfNr,
        SZUMZID: row.SZUMZID,
        autoFK: eintrag?.FK,
      },
      Pruefart: { Bezeichnung: row.Pruefart, ID: undefined },
      mangelGr: {
        Lage: { Bezeichnung: row.Lage, ID: undefined },
        Mangel: { Bezeichnung: row.Mangel, ID: undefined },
        Instandsetzung: { Bezeichnung: row.Instandsetzung, ID: undefined },
        Umfang: row.Umfang,
        Mengeneinheit: row.Mengeneinheit,
        FK: row.FK,
        Betriebsgefahr: row.Betriebsgefahr,
        Abteilung: row.Abteilung,
        Equipment: row.Equipment,
        LVPos: row.LVPos,
        Bemerkung: row.Bemerkung,
      },
    });

    await this.setEveryFilterList(row);

    const mehListe = this.meh();
    const meh = row.Mengeneinheit;
    if (meh && !mehListe?.find(m => m.Bezeichnung == meh)) this.selectedMangel().addMeh = true;

    // this.clrForm().markAsTouched();

    this.isLoading.set(false);
    setTimeout(() => (this.isInitializing = false));
  }

  /** @listens typ_Line_Point @listens formValue: gattung/stamm/pruefart/von usw.*/
  private catchChangesEffects() {
    const injector = inject(Injector);
    afterNextRender({
      //* im constructor ist baumKette() noch nicht erreichbar; Alternative zum ngOnInit:
      earlyRead: () => {
        for (const mode of this.baumKette().keys()) {
          if (mode == 'FK') break;
          effect(
            //* für jeden Schritt in baumKette eigenen Effect erstellen, um für Änderungen nicht vergleichen zu müssen
            () => {
              const value = this.formValue[mode]();
              if ((mode == 'Pruefart' && !this.nIO()) || this.isInitializing) return;
              untracked(() => this.filterForValues(mode, value));
            },
            { injector }
          );
        }
      },
    });

    effect(() => {
      if (this.nIO() || this.isInitializing) return;
      this.hiddenGr.patchValue({
        SZUMZID: 0,
        autoFK: null,
        Faktor: 0,
      });
      this.mangelGr.reset();
    });

    effect(() => {
      const von = this.formValue.von();
      if (this.isInitializing) return;
      untracked(() => {
        const id = this.formValue.Gattung()?.ID;
        const nextValue = this.findStamm_fromSpez(id, von);
        if (nextValue) this.gleisGr.controls.Stamm.setValue(nextValue);
      });
    });

    effect(() => {
      if (!this.gefahrFK() || this.formValue.FK() == null || this.isInitializing) return;
      //* FK == minFK => Betriebsgefahr
      if (this.formValue.FK() == this.minFK()) this.mangelGr.controls.Betriebsgefahr.setValue(true);
    });
    effect(() => {
      if (!this.gefahrFK() || !this.formValue.Betriebsgefahr() || this.isInitializing) return;
      //* Betriebsgefahr => FK auf minFK setzen
      this.mangelGr.controls.FK.setValue(this.minFK());
    });

    effect(() => {
      if (!this.sperrFK()) return;
      //* FK kleiner als autofk ? => sperrungFK für Meldung && Zurücksetzen FK -> autofk
      const fk = this.formValue.FK();
      untracked(() => {
        const autofk = this.formValue.autoFK();
        if (autofk == null) return;
        if (fk > autofk) {
          this.sperrungFK.set(true);
          this.mangelGr.controls.FK.setValue(autofk);
        } else if (fk == null || fk < autofk) this.sperrungFK.set(false);
      });
    });

    effect(() => {
      (this.formValue.von(), this.formValue.bis(), this.formValue.Umfang()); //* catch Changes
      if (this.isInitializing) return;
      untracked(async () => {
        const values = this.listFiltered.FK();
        if (values.length > 1) {
          const nextValue = this.findFK_beiBedingung(values);
          if (nextValue?.ID == this.formValue.SZUMZID()) return;
          const eintrag = await this.getBaumEintrag(nextValue?.ID);
          const fk = this.setFromBaumEintrag(eintrag);
          if (fk != null) this.mangelGr.controls.FK.setValue(fk);
        }
      });
    });
  }
  //#endregion

  //#region MangelBaum Logik()
  /** @method handelt Situationen, wenn Werte gleichzeitig geändert wurden (selected, reset auf null) */
  private async setEveryFilterList(row: MangelRowModel) {
    for (const [mode, nextMode] of this.baumKette().entries()) {
      if (mode == 'FK') break;
      if (row) {
        const bez = row[mode];
        const value = this.matchItemByBezeichnung(bez, mode);
        if (value) this.formTopGroup.get(MODE_CONTROL[mode]).setValue(value);
        if (mode == 'Pruefart' && !this.nIO()) return;
        await this.getFilterList(nextMode, value);
      } else {
        this.getFilterList(nextMode, null);
      }
    }
  }

  private matchItemByBezeichnung(bez: string, mode: Exclude<BaumMode, 'FK'>) {
    if (!bez) return null;
    const zugeordnet = this.listFiltered[mode]().find(item => item.Bezeichnung == bez);
    return zugeordnet ?? this.listRest[mode]().find(item => item.Bezeichnung == bez);
  }

  /**
   * @method
   * - ruft filterBaum auf, um values zu erstellen
   * - für FK:
   *   - sets fk if zugeordnet: single or nach Bedingungen;
   *   - resets SZUMZID, autoFK;
   *   - sets Abteilung, Equipment if no user value
   * - für Stamm (Gattung wurde geändert):
   *   - singleOrNull() oder über Spezifikationen;
   *   - sets value regardless of user value
   * - für andere:
   *   - matches user value to filtered List (for correct route IDs) or takes value without route;
   *   - if no user value -> singleOrNull()
   *   - always sets value to filterKette for nextMode
   */
  private async filterForValues(mode: BaumMode, value: MangelBaumNode | BaseItem | null) {
    if (mode == 'Gattung') {
      for (const nextMode of this.baumKette().values()) {
        this.formTopGroup.get(MODE_CONTROL[nextMode]).reset(null, { emitEvent: false });
      }
    }

    const nextMode = this.baumKette().get(mode);
    const values = await this.getFilterList(nextMode, value);

    if (nextMode == 'FK') {
      const endNode = this.singleOrNull(values) ?? this.findFK_beiBedingung(values);
      const eintrag = await this.getBaumEintrag(endNode?.ID);
      const fk = this.setFromBaumEintrag(eintrag);
      if (fk !== null) this.mangelGr.controls.FK.setValue(fk);
    } else {
      const control = this.formTopGroup.get(MODE_CONTROL[nextMode]);
      let nextValue: MangelBaumNode | BaseItem | null;
      if (nextMode == 'Stamm') {
        nextValue = this.singleOrNull(values) ?? this.findStamm_fromSpez(value?.ID, this.formValue.von());
      } else {
        nextValue = this.matchItemById(values, control.value) ?? this.singleOrNull(values);
      }
      control.setValue(nextValue);
    }
  }

  private matchItemById(values: MangelBaumNode[], value: MangelBaumNode | BaseItem): MangelBaumNode | BaseItem {
    if (!value) return null;
    const matched = values.find(row => row.ID == value.ID);
    return matched ?? { ID: value.ID, Bezeichnung: value.Bezeichnung };
  }

  /**
   * @method
   * - gets values vom Server, die zu diesem MangelBaumNode zugeordnet sind
   * - sets values for filteredList
   */
  private async getFilterList(mode: BaumMode, value: MangelBaumNode | BaseItem | null) {
    try {
      const values =
        value && 'Route' in value ? await this.apiService.getMangelBaum(this.OTYPID(), joinIds(value)) : [];
      this.listFiltered[mode].set(values);
      return values;
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangelbaum'));
      return [];
    }
  }

  private async getBaumEintrag(szumzid: number | null) {
    if (!szumzid) return null;
    try {
      return await this.apiService.getMangelBaumEintrag(szumzid);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangelbaum Eintrag'));
    }
  }

  private singleOrNull(values: MangelBaumNode[]): MangelBaumNode | null {
    return values.length == 1 ? values[0] : null;
  }

  /**
   * @method sets
   * - SZUMZID, autoFK,
   * - Gattung in hiddenGr für Punkte,
   * - Abteilung u. Equipment, falls eingeschaltet
   * - Mengeneinheit, falls nicht vom User ausgewählt wurde
   * @param row - der einzige Row oder nach Bedingungen gefunden oder null
   * @returns FK
   */
  private setFromBaumEintrag(row: MangelBaumNodeModel | null): number | null {
    const fk = row?.FK;
    this.hiddenGr.patchValue({
      autoFK: fk,
      SZUMZID: row?.ID ?? 0,
    });

    const { Abteilung: abt, Equipment: eq, Mengeneinheit: meh } = this.mangelGr.controls;
    if (abt.pristine) abt.setValue(row?.Abteilung);
    if (eq.pristine) eq.setValue(row?.Equipment);
    if (meh.pristine)
      meh.setValue(row?.MengeneinheitID ? this.meh().find(m => m.ID == row.MengeneinheitID)?.Bezeichnung : null);

    this.mangelGr.patchValue({
      ZFeld1: row?.ZFeld1,
      ZFeld2: row?.ZFeld2,
      ZFeld3: row?.ZFeld3,
      ZFeld4: row?.ZFeld4,
      ZFeld5: row?.ZFeld5,
    });

    return this.rangeFK().includes(fk) ? fk : null;
  }

  /**
   * @method durchsucht Spezifikationen nach Abschnitt, der Startmeter der Mangel enthält
   * @param id von Gattung (intID hier)
   * @returns Wert der Stammdaten, basierend auf Gattung (strValue02 | strValue03 | strValue04 | strValue05)
   */
  private findStamm_fromSpez(id: number, von: number): MangelBaumNode | BaseItem | null {
    const colName = GATTUNG_SPEZ[id] as Exclude<keyof SpezRow, 'von' | 'bis'>;
    if (von == null || !colName) return null;
    const spez = this.spezResouce.value();
    const stamm = spez.find(row => {
      switch (this.direction()) {
        case 1:
          return row.von <= von && von < row.bis;
        case -1:
          return row.von >= von && von > row.bis;
      }
    })?.[colName];

    return stamm ?
        (this.listFiltered.Stamm().find(row => row.Bezeichnung == stamm) ??
          this.listRest.Stamm().find(row => row.Bezeichnung == stamm))
      : null;
  }

  /**
   * @method durchsucht mangelBaum mit allen möglichen FK:
   * - für Linieobjekte nach prozent von Länge der Mangel
   * - für Weiche nach prozent von gesamten Anzahl dieser Prüfart in Bauartgruppe
   * - für andere Punktobjekte - immer erster Row
   */
  private findFK_beiBedingung(rows: MangelBaumNode[]): MangelBaumNode {
    if (!rows.length) return null;

    const defaultRow = rows.find(row => row.Bezeichnung.length <= 2);
    const umfang = this.formValue.Umfang();

    if (!umfang) return defaultRow;

    let total = umfang;

    switch (this.typ_Line_Point()) {
      case 1:
        return rows[0];
      case 3:
        const pruefart = this.formValue.Pruefart()?.Bezeichnung;
        const anzahl = this.bauartgruppeRs.value()[pruefart];
        if (anzahl) total = anzahl;
        break;
      case 2:
      case 4:
        if (this.linieGr.valid) {
          const faktor = this.formValue.Faktor() || 1;
          const länge = Math.abs(this.formValue.bis() - this.formValue.von()) || 1;
          total = länge * faktor * 1.6667;
        }
        break;
    }

    const prozent = umfang > total ? 100 : (umfang / total) * 100;
    const meh = this.formValue.Mengeneinheit();

    return (
      rows.find(({ Bezeichnung }) => {
        //* operator value unit (then ',' for bed2 or ']' for end)
        const reg = /(>=|<=|>|<) (\d+) ([^,\]]+)/g;

        const matches = [...Bezeichnung.matchAll(reg)];
        if (!matches.length) return false;
        const bedingungen = matches.map(match => ({
          operator: match[1] as Bedingung,
          value: +match[2],
          unit: match[3] || '%',
        }));
        const [bed1, bed2] = bedingungen;
        const value = bed1.unit == '%' && meh != '%' ? prozent : umfang;
        let passt = onBedingung(bed1.operator, value, bed1.value);
        if (passt && bed2) passt = onBedingung(bed2.operator, value, bed2.value);
        return passt;
      }) || defaultRow
    );
  }
  //#endregion

  //#region Buttons()
  protected copyLinieValue(copyTo: 'von' | 'bis', copyFrom: 'von' | 'bis') {
    const value = this.formValue[copyFrom]();
    if (value == null) return;
    this.linieGr.get(copyTo).setValue(value);
  }

  protected async findPositionAndAutofill(mode: 'von' | 'bis') {
    this.geoState.set(ClrLoadingState.LOADING);
    const snapped = await this.geoAutofill.getSnappedLength();
    this.geoState.set(ClrLoadingState.DEFAULT);
    if (snapped === null) return;

    const position = calcPosition({
      snapped,
      start: this.startmeter(),
      end: this.endmeter(),
      direction: this.direction(),
    });

    this.linieGr.controls[mode].setValue(position);
  }

  protected setFaktor(faktor: number) {
    faktor ||= 1;
    this.hiddenGr.controls.Faktor.setValue(faktor);
    const länge = Math.abs(this.formValue.bis() - this.formValue.von()) || 1;
    // * linieGr immer valid hier u. 1<umfang<=100; weil button sonst disabled
    const umfang = this.mangelGr.controls.Umfang;
    const anzahl = länge * faktor * 1.6667;
    const value = Math.round((umfang.value * anzahl) / 100);
    umfang.setValue(value);
  }

  protected async openHelper() {
    const id = this.formValue.Mangel().ID;
    this.openedHelperId.set(id);
    this.isHelperModalOpen.set(true);
  }

  protected updateMEH(list: BaseItem[]) {
    this.selectResource.update(select => ((select.Mengeneinheiten = list), { ...select }));
    const meh = this.mangelGr.controls.Mengeneinheit;
    if (meh.value && !list.find(m => m.Bezeichnung == meh.value)) meh.setValue(null);
  }

  protected async addMangelTree() {
    try {
      const sendObj = await this.packMangelBaumNode();
      const { id, exist } = await this.apiService.setMangelBaumEintrag(this.OTYPID(), sendObj, true);
      await this.apiService.updateMangelSZUMZID(this.OMAZSID(), id);

      this.selectedMangel().SZUMZID = id;
      this.canCreateMangelBaumNode.set(false);

      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      if (exist) this.toastr.info(`${this.mrTranslate.transform('Eintrag bereits existiert')}: ${id}`);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangelbaum'));
    }
  }
  //#endregion

  //#region API()
  protected addMangel() {
    this.preview()?.clearPreview();
    this.selected.set([]);

    if (objtype.isPoint(this.typ_Line_Point())) {
      const Pruefart = this.formValue.Pruefart();
      const lfNr = this.formValue.lfNr();
      this.resetForm();
      this.hiddenGr.controls.lfNr.setValue(lfNr + 1);
      this.formTopGroup.controls.Pruefart.setValue(Pruefart);
    } else this.resetForm();
  }

  protected saveIfValid() {
    if (this.formTopGroup.invalid) {
      this.clrForm().markAsTouched();
      return;
    }
    if (this.isInspektion()) this.editMangel();
    else this.saveMangel();
  }

  /** @method Speichern außerhalb von Inspektion */
  private async saveMangel() {
    this.saveState.set(ClrLoadingState.LOADING);

    try {
      const files = await this.preview().packUnsavedFiles();
      const mangel = this.rowModel;

      const row = await this.apiService.sendNewMangel(mangel, files);

      this.newlyCreated.update(({ columns, rows }) => {
        if (columns.length == 0) columns = Object.keys(row).map(id => ({ id }));
        return { columns, rows: [...rows, row] };
      });

      this.resetForm();
      this.preview()?.clearPreview();
      this.dataRefresh?.next();
      this.dispatcher.dispatch(
        createdMangel({
          omazsid: +row.OMAZSID,
          ostammid: mangel.OSTAMMID,
        })
      );
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'), this.mrTranslate.transform('Mangel'));
      this.saveState.set(ClrLoadingState.SUCCESS);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangel'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  /** @method Speichern in Inspektion */
  private async editMangel() {
    this.saveState.set(ClrLoadingState.LOADING);
    this.isSaving = true;

    try {
      const preview = this.preview();
      if (preview.disabled()) preview.clearPreview();

      const files = await preview.packUnsavedFiles();
      const savedFiles = preview.savedNames();

      const { OMAZSID, table } = await this.apiService.updateMangel(this.rowModel, files, savedFiles);

      this.maengelRs.set(table);
      this.selected.set(table.rows.filter(row => row.OMAZSID == OMAZSID));
      this.cachedMedia.reloadAfterSaved(OMAZSID, preview, this.deletedFiles as string[]);
      this.deletedFiles = [];
      await this.resetLfNr();
      this.formTopGroup.markAsPristine();
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'), this.mrTranslate.transform('Mangel'));
      this.saveState.set(ClrLoadingState.SUCCESS);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangel'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    } finally {
      setTimeout(() => (this.isSaving = false));
    }
  }

  private async updateStatus() {
    this.saveState.set(ClrLoadingState.LOADING);
    try {
      await this.apiService.updateMangelStatus(this.OMAZSID());
      this.selectedMangel().iO = true;
      this.selectedMangel().Status = BackendLocaleDatePipe.now;
      this.maengelRs.update(table => ({ ...table }));

      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.saveState.set(ClrLoadingState.SUCCESS);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangel'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  protected async deleteMangel() {
    const ok = await this.confirmService.danger(this.mrTranslate.transform('Mangel wirklich löschen?'));
    if (!ok) return;

    try {
      await this.apiService.deleteMangel(this.OMAZSID());
      this.cachedMedia.deleteInCache(this.OMAZSID(), this.preview());

      this.maengelRs.update(table => {
        table.rows = table.rows.filter(row => row !== this.selectedMangel());
        return { ...table };
      });
      this.selected.set([]);
      this.resetForm();
      await this.resetLfNr();

      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangel'));
    }
  }

  /** @method resets lfNr: 1, 2, 3, ... */
  private async resetLfNr() {
    try {
      await Promise.all(
        this.table().rows.map((row, index) => {
          const lfNr = index + 1;
          if (row.lfNr == lfNr) return;
          row.lfNr = lfNr; // für Anzeige
          return this.apiService.updateMangelLfNr(row.OMAZSID, lfNr);
        })
      );
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Mangel lfNr'));
    }
  }
  //#endregion

  //#region from Inspektion()
  public refreshSpez() {
    this.spezResouce.reload();
    this.bauartgruppeRs.reload();
  }

  public async checkValid() {
    if (!this.isMangelValidStatus())
      this.toastr.error(
        this.mrTranslate.transform('Es gibt noch offene Mängel aus der letzten Inspektion...!'),
        this.mrTranslate.transform('Mangel')
      );
    return this.isMangelValidStatus();
  }

  public async checkPunktValid() {
    await this.findDuplicates();
    return true;
  }

  public checkLinieValid(startmeter: number, endmeter: number) {
    if (!objtype.isLine(this.typ_Line_Point())) return true;

    this.stammData.set({ startmeter, endmeter });
    const valid = this.isLinieValid(this.table().rows, startmeter, endmeter);
    if (!valid)
      this.toastr.error(
        this.mrTranslate.transform('Berücksichtigen Sie die Start- & Endmeter'),
        this.mrTranslate.transform('Mangel')
      );
    return valid;
  }

  public async deleteAlteMesswerteGleis() {
    this.selected.set([]);
    try {
      const { found: rowsMesswerte, rest } = partition(this.table().rows, item => /^messwerte$/i.test(item.Gattung));
      if (!rowsMesswerte.length) {
        this.toastr.info(this.mrTranslate.transform('Zu den Messwerten wurden keine Mängel gefunden'));
        return true;
      }
      await this.apiService.deleteMangel(...rowsMesswerte.map(row => row.OMAZSID));
      this.maengelRs.update(table => ({ ...table, rows: rest }));

      await this.resetLfNr();
      this.dataRefresh?.next();
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      return true;
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Messungen'));
      return false;
    }
  }

  public async clearMessungsVerletzungen() {
    try {
      const OMAZSIDs = this.table()
        .rows.filter(row => isMessEintrag(row.Pruefart))
        .map(row => row.OMAZSID);
      const maengel = await this.apiService.updateMangelToEmpty({
        OTYPID: this.OTYPID(),
        OSTAMMID: this.OSTAMMID(),
        OPRUEFID: this.OPRUEFID(),
        typ_Line_Point: this.typ_Line_Point(),
        OMAZSIDs,
      });
      this.maengelRs.set(maengel);
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Messungen'));
    }
  }

  public async applyChecklist(name: string, mode: 'ersetzen' | 'ergänzen') {
    if (!name) return;
    const ok = () =>
      this.confirmService.danger(
        this.mrTranslate.transform(
          'Die vorhandenen Prüfeinträge werden durch die neue Checkliste ersetzt. Möchten Sie fortsetzen?'
        )
      );
    if (mode == 'ersetzen' && this.table().rows.length && !(await ok())) return;

    try {
      const maengel = await this.apiService.updateInspektionToChecklist({
        OTYPID: this.OTYPID(),
        OSTAMMID: this.OSTAMMID(),
        OPRUEFID: this.OPRUEFID(),
        typ_Line_Point: this.typ_Line_Point(),
        Titel: name,
        maengel: mode == 'ergänzen' ? [...this.table().rows] : null,
      });

      this.maengelRs.set(maengel);
      let successMessage: string;
      switch (mode) {
        case 'ersetzen': {
          this.selected.set([]);
          this.preview()?.clearPreview();
          this.cachedMedia.resetCache();
          successMessage = 'Checkliste wurde geöffnet';
          break;
        }
        case 'ergänzen': {
          successMessage = 'Prüfarten wurden ergänzt';
        }
      }

      this.toastr.success(this.mrTranslate.transform(successMessage), this.mrTranslate.transform('Checkliste'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Checkliste'));
    }
  }

  public get pruefarten() {
    const pruefarten = [...new Set(this.table().rows.map(row => row.Pruefart))];
    const allItems = this.selectResource.value().Prüfarten;
    const matched = matchBy(pruefarten, allItems, 'Bezeichnung');
    return matched;
  }
  public get mangelRows(): MangelRowType[] {
    return [...this.table().rows];
  }
  //#endregion

  //#region helpers()
  /** @requires InjectionContext*/
  private restOfItems(mode: BaumMode, key: keyof BaumSelect, gattungIdFn?: () => number) {
    return computed(() => {
      const select = this.selectResource.value();
      return differenceBy(
        key == 'StammItems' ? select.StammItems[gattungIdFn()] : select[key],
        this.listFiltered[mode](),
        'ID'
      );
    });
  }

  /** @requires InjectionContext*/
  private catchFormValues() {
    const map: { [Key in keyof MangelFormControls]?: Signal<MangelFormControls[Key]> } = {};
    for (const key in this.formTopGroup.controls) {
      const path = key as keyof MangelFormModel;
      switch (path) {
        case 'Pruefart':
        case 'nIO': {
          map[path] = this.onChange(this.formTopGroup.controls[path]);
          break;
        }
        case 'mangelGr':
        case 'hiddenGr':
        case 'linieGr':
        case 'gleisGr': {
          for (const [key, control] of Object.entries(this.formTopGroup.controls[path].controls)) {
            const path = key as keyof MangelRowModel;
            map[path] = this.onChange(control);
          }
          break;
        }
      }
    }
    return map;
  }

  /** @requires InjectionContext*/
  private onChange(control: FormControl) {
    return toSignal(control.valueChanges, { initialValue: null });
  }

  private resetForm() {
    if (this.formGroup().form) this.formGroup().resetForm();
  }

  /**
   * @method sucht gleiche Prüfarte und behaltet:
   * - bei 2 und mehr n.I.O. - `alle`
   * - bei 1 i.O. und 1 n.I.O. - `mit Mangel`
   * - bei 2 und mehr i.O - `erste`
   */
  private async findDuplicates() {
    if (!objtype.isPoint(this.typ_Line_Point())) return;
    if (!this.table()?.rows?.length) return;

    try {
      const keepMap = new Map<string, number | number[]>();
      // * Durchgeht rows und erstellt Map mit Prüfart und
      // - mehrere ID im Array (n.I.O.)
      // - einem ID (i.O.)
      this.table().rows.forEach(({ Pruefart, nIO, OMAZSID }) => {
        const id = keepMap.get(Pruefart);
        if (!id)
          // * erstmal alle gespeichert, i.O. und n.I.O.
          // - bei n.I.O. ID in Array, da es mehrere möglich sind
          keepMap.set(Pruefart, nIO ? [OMAZSID] : OMAZSID);
        else if (nIO) {
          // * bei zweitem Mal nur speichern, wenn n.I.O.
          // - im Array hinzufügen, wenn letzte war auch n.I.O., sonst ersetzen
          if (Array.isArray(id)) id.push(OMAZSID);
          else keepMap.set(Pruefart, [OMAZSID]);
        }
      });

      // * get alle ID in Array, [[1, 2], 3] flat() zu [1, 2, 3]
      const keepIDs = [...keepMap.values()].flat();
      if (keepIDs.length == this.table().rows.length) return;

      const deleteIDs: number[] = [];
      // * filtern rows, die bleiben, gleichzeitig erstellen IDs von rows zum Löschen
      this.maengelRs.update(table => {
        table.rows = table.rows.filter(({ OMAZSID }) => {
          if (keepIDs.includes(OMAZSID)) return true;
          deleteIDs.push(OMAZSID);
          return false;
        });
        return { ...table };
      });
      await this.apiService.deleteMangel(...deleteIDs);
      await this.resetLfNr();
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Doppelte Mangel'));
    }
  }

  private activateControlIf(path: keyof MangelFormModel, enabled: boolean) {
    const control = this.formTopGroup.get(path);
    if (enabled) control.enable();
    else control.disable();
  }

  private async packMangelBaumNode() {
    const { Gattung, Stamm, Pruefart, Lage, Mangel, Instandsetzung, FK, Abteilung, Equipment, Mengeneinheit } =
      this.formValue;
    let GattungID: number, StammID: number, LageID: number;
    switch (objtype.is(this.typ_Line_Point())) {
      case 'point': {
        GattungID = 0;
        StammID = 0;
        LageID = await this.getOrCreateMangelTreeId('Lage', Lage());
        break;
      }
      case 'line': {
        GattungID = Gattung().ID;
        StammID = Stamm().ID;
        LageID = 0;
        break;
      }
    }
    const PruefartID = await this.getOrCreateMangelTreeId('Pruefart', Pruefart());
    const MangelID = await this.getOrCreateMangelTreeId('Mangel', Mangel());
    const InstandsetzungID = await this.getOrCreateMangelTreeId('Instandsetzung', Instandsetzung());
    const meh = Mengeneinheit() ? this.meh().find(m => m.Bezeichnung == Mengeneinheit()) : null;

    const sendObj: MangelBaumNodeModel = {
      ID: 0,
      GattungID,
      StammID,
      PruefartID,
      LageID,
      MangelID,
      InstandsetzungID,
      FK: FK(),
      MengeneinheitID: meh?.ID ?? 0,
      Abteilung: Abteilung(),
      Equipment: Equipment(),
      Bedingung: false,
      AndBedingung: false,
    };
    return sendObj;
  }

  private async getOrCreateMangelTreeId(mode: BaumMode, value: BaseItem) {
    return value.ID ?? (await this.apiService.setBaseItems(BAUM_MODE_TABELLE[mode], value, this.OTYPID()));
  }

  protected close() {
    this.closedialog.next(true);
  }
  //#endregion
}

const BAUM_MODE_TABELLE = {
  Pruefart: 'SPRUE',
  Lage: 'SLAGE',
  Mangel: 'SMANG',
  Instandsetzung: 'SINST',
} as const satisfies Partial<Record<BaumMode, BaseDataTable>>;

const GATTUNG_SPEZ = {
  1: 'strValue02',
  3: 'strValue03',
  2: 'strValue04',
  5: 'strValue05',
} as const satisfies Record<number, keyof SpezRow>;

const MODE_CONTROL = {
  Gattung: 'gleisGr.Gattung',
  Stamm: 'gleisGr.Stamm',
  Pruefart: 'Pruefart',
  Lage: 'mangelGr.Lage',
  Mangel: 'mangelGr.Mangel',
  Instandsetzung: 'mangelGr.Instandsetzung',
  FK: 'mangelGr.FK',
} as const satisfies { [K in BaumMode]: K | `${keyof MangelFormModel}.${K}` };

function onBedingung(bedingung: Bedingung, value: number, grenze: number) {
  switch (bedingung) {
    case '>':
      return value > grenze;
    case '>=':
      return value >= grenze;
    case '<':
      return value < grenze;
    case '<=':
      return value <= grenze;
    default:
      return false;
  }
}

export function isMessEintrag(po: string): boolean {
  return (
    po.includes('Höhenlage') ||
    po.startsWith('ZuPrf') ||
    [
      'Überhöhung',
      'Querhöhe',
      'Spurweite',
      'Spurweiten',
      'Leitweiten',
      'Leitkantenabstand',
      'Rillenweiten',
      'Rillentiefen',
      'Durchfahrrillen',
      'Durchfahrrille',
      'Schienenbreiten',
      'Spurunterschiede',
      'Verwindung',
      'Schienentiefen',
    ].some(po1 => po.includes(po1))
  );
}

export function IsMangelValid(mangel: MangelModel, type: 'line' | 'point'): boolean {
  if (type === 'point' && !mangel.nIO) return true;

  const hasValidType = type === 'point' ? !!mangel.Lage : !!(mangel.Gattung && mangel.Stamm);

  return hasValidType && !!(mangel.Pruefart && mangel.Mangel && mangel.Instandsetzung);
}

export interface CreatedMangel {
  omazsid: number;
  ostammid: number;
}

export const createdMangel = event('[Mangel Erfassung] Created Mangel', type<CreatedMangel>());
